import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Input } from "../../../components/input";
import { RequiredLabel } from "../../../components/form/RequiredLabel";
import { ImageUploadCropper } from "../../../components/image-upload-cropper";
import { SearchSelect } from "../../../components/search-select-v2";
import { useMerchantCategoryParentOptions } from "../hooks/useMerchantCategoryParentOptions";
import type {
  MerchantCategory,
  MerchantCategoryParentOption,
  MerchantCategoryPayload,
} from "../types/merchantCategory.types";

const merchantCategorySchema = z.object({
  identifier: z.string().max(255),
  description: z.string().trim().min(1, "Name is required.").max(4000),
  parentCategoryId: z.string().uuid().or(z.literal("")),
  iconFile: z.instanceof(File).nullable(),
  hoverIconFile: z.instanceof(File).nullable(),
});

type MerchantCategoryEditorValues = z.input<typeof merchantCategorySchema>;
type MerchantCategoryEditorOutput = z.output<typeof merchantCategorySchema>;

type MerchantCategoryEditorProps = {
  formId: string;
  category: MerchantCategory | null;
  initialParentOption?: MerchantCategoryParentOption;
  isOpen: boolean;
  onSubmit: (payload: MerchantCategoryPayload) => Promise<void>;
};

const formatParentOptionLabel = (option: MerchantCategoryParentOption) =>
  option.identifier ? `${option.description} (${option.identifier})` : option.description;

const noParentCategoryValue = "__no_parent_category__";

type ParentCategorySelectProps = {
  value: string;
  currentParentOption?: MerchantCategoryParentOption;
  excludeId?: string;
  enabled: boolean;
  onChange: (value: string) => void;
};

function ParentCategorySelect({
  value,
  currentParentOption,
  excludeId,
  enabled,
  onChange,
}: ParentCategorySelectProps) {
  const { options, error, isLoading } = useMerchantCategoryParentOptions({
    enabled,
    excludeId,
  });
  const parentOptions = useMemo(() => {
    if (!currentParentOption || options.some((option) => option.id === currentParentOption.id)) {
      return options;
    }
    return [currentParentOption, ...options];
  }, [currentParentOption, options]);
  const selectOptions = useMemo(
    () => [
      { value: noParentCategoryValue, label: "No parent category" },
      ...parentOptions.map((option) => ({
        value: option.id,
        label: formatParentOptionLabel(option),
      })),
    ],
    [parentOptions],
  );

  return (
    <div className="space-y-1">
      <SearchSelect
        value={value || noParentCategoryValue}
        onChange={(nextValue) => onChange(nextValue === noParentCategoryValue ? "" : nextValue)}
        options={selectOptions}
        placeholder="No parent category"
        ariaLabel="Parent category"
        searchPlaceholder="Search parent categories"
        emptyMessage="No parent categories found."
        showClear={Boolean(value)}
        contentClassName="z-[1401]"
        disabled={isLoading}
      />
      {error ? <span className="text-sm text-red-500">{error}</span> : null}
    </div>
  );
}

export function MerchantCategoryEditor({
  formId,
  category,
  initialParentOption,
  isOpen,
  onSubmit,
}: MerchantCategoryEditorProps) {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MerchantCategoryEditorValues, undefined, MerchantCategoryEditorOutput>({
    resolver: zodResolver(merchantCategorySchema),
    defaultValues: {
      identifier: "",
      description: "",
      parentCategoryId: "",
      iconFile: null,
      hoverIconFile: null,
    },
  });

  const resetForm = () =>
    reset({
      identifier: category?.identifier ?? "",
      description: category?.description ?? "",
      parentCategoryId: category?.parent_merchant_category_id ?? "",
      iconFile: null,
      hoverIconFile: null,
    });

  useEffect(() => {
    if (isOpen) resetForm();
  }, [category, isOpen]);

  return (
    <form
      id={formId}
      onSubmit={(event) =>
        void handleSubmit(async (values) => {
          await onSubmit({
            identifier: values.identifier.trim() || null,
            description: values.description.trim(),
            parent_merchant_category_id: values.parentCategoryId || null,
            icon_file: values.iconFile,
            icon_hover_file: values.hoverIconFile,
          });
          resetForm();
        })(event)
      }
      className="space-y-6 p-6"
    >
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Category icons</h3>
          <p className="mt-1 text-sm text-slate-500">
            Add an optional default icon and a hover-state variation.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Controller
            control={control}
            name="iconFile"
            render={({ field }) => (
              <ImageUploadCropper
                value={field.value ?? category?.icon_url ?? null}
                onChange={field.onChange}
                aspect={1}
                title="Default icon"
                description="Shown in the standard category state."
                outputFileName="merchant_category_icon.jpg"
                emptyClassName="min-h-[170px] py-4"
                previewClassName="gap-3"
              />
            )}
          />
          <Controller
            control={control}
            name="hoverIconFile"
            render={({ field }) => (
              <ImageUploadCropper
                value={field.value ?? category?.icon_hover_url ?? null}
                onChange={field.onChange}
                aspect={1}
                title="Hover icon"
                description="Shown when the category is hovered."
                outputFileName="merchant_category_hover_icon.jpg"
                emptyClassName="min-h-[170px] py-4"
                previewClassName="gap-3"
              />
            )}
          />
        </div>
      </section>

      <section className="space-y-4 border-t border-slate-100 pt-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Category details</h3>
          <p className="mt-1 text-sm text-slate-500">
            Choose a parent only when this should be a child category.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            <RequiredLabel required>Category name</RequiredLabel>
            <Input
              {...register("description")}
              placeholder="e.g. Food & dining"
              maxLength={4000}
            />
            {errors.description ? (
              <span className="text-sm text-red-500">{errors.description.message}</span>
            ) : null}
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            <span>Identifier</span>
            <Input
              {...register("identifier")}
              placeholder="e.g. food-dining"
              maxLength={255}
            />
            <span className="text-xs font-normal text-slate-500">
              Optional code used for search and integrations.
            </span>
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium md:col-span-2">
            <span>Parent category</span>
            <Controller
              control={control}
              name="parentCategoryId"
              render={({ field }) => (
                <ParentCategorySelect
                  value={field.value}
                  currentParentOption={initialParentOption}
                  excludeId={category?.id}
                  enabled={isOpen}
                  onChange={field.onChange}
                />
              )}
            />
            <span className="text-xs font-normal text-slate-500">
              Leave empty to create a top-level category.
            </span>
            {errors.parentCategoryId ? (
              <span className="text-sm text-red-500">{errors.parentCategoryId.message}</span>
            ) : null}
          </label>
        </div>
      </section>
    </form>
  );
}
